import React from "react";
import PropTypes from "prop-types";
import { WEEK_ORDER, adjustmentFor, colorOf, dayShort, describeAdjustment, formatEntryTime } from "../ticketSets";

const weekdayOf = (isoDate) => new Date(`${isoDate}T12:00:00`).getDay();
const UNDECIDED = "undecided";

export const weeklyBands = (sets, cover) => {
  const byKey = Object.fromEntries(sets.map((set) => [set.key, set]));
  const columns = WEEK_ORDER.filter((day) => cover.scheduled.some((date) => weekdayOf(date) === day));

  const cellFor = (day, time) => {
    const tally = {};
    let total = 0;
    cover.scheduled
      .filter((date) => weekdayOf(date) === day)
      .forEach((date) => {
        const slot = cover.dates[date].slots.find((candidate) => candidate.time === time);
        if (!slot) return;
        total += 1;
        const key = slot.key || ((slot.undecided || []).length > 0 ? UNDECIDED : "");
        tally[key] = (tally[key] || 0) + 1;
      });
    if (total === 0) return null;
    const top = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
    const set = byKey[top] || null;
    const adjustment = set ? adjustmentFor(set, time) : null;
    return {
      key: set ? top : null,
      undecided: top === UNDECIDED,
      varies: total - tally[top],
      sub: [adjustment && describeAdjustment(adjustment), set && set.ageRestriction].filter(Boolean).join(" · "),
    };
  };

  const bands = [];
  cover.times.forEach((time) => {
    const cells = columns.map((day) => cellFor(day, time));
    const signature = JSON.stringify(cells.map((cell) => cell && [cell.key, cell.undecided, cell.sub, cell.varies > 0]));
    const last = bands[bands.length - 1];
    if (last && last.signature === signature) last.times.push(time);
    else bands.push({ signature, times: [time], cells });
  });
  return { columns, bands };
};

const bandLabel = (times) =>
  times.length === 1 ? formatEntryTime(times[0]) : `${formatEntryTime(times[0])} – ${formatEntryTime(times[times.length - 1])}`;

const WeeklyCell = ({ cell, label, color, onJumpToSet }) => {
  const title = `${label}${cell.varies > 0 ? ` · different on ${cell.varies} date${cell.varies === 1 ? "" : "s"}` : ""}`;
  const content = (
    <>
      <span>{label}</span>
      {(cell.sub || cell.varies > 0) && (
        <span className="series-set-weekly-sub">{[cell.sub, cell.varies > 0 && "varies"].filter(Boolean).join(" · ")}</span>
      )}
    </>
  );
  if (!cell.key) {
    return (
      <span className="series-set-weekly-cell series-set-uncovered" title={title}>
        {content}
      </span>
    );
  }
  return (
    <button type="button" className={`series-set-weekly-cell series-set-color-${color}`} title={title} onClick={() => onJumpToSet(cell.key)}>
      {content}
    </button>
  );
};

WeeklyCell.propTypes = {
  cell: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  onJumpToSet: PropTypes.func.isRequired,
};

const WeeklyEntryTimes = ({ sets, cover, onJumpToSet }) => {
  const { columns, bands } = weeklyBands(sets, cover);
  const nameOf = (key) => (sets.find((set) => set.key === key) || {}).name;
  const cellLabel = (cell) => {
    if (cell.key) return nameOf(cell.key);
    return cell.undecided ? "Needs a decision" : "Not covered";
  };

  return (
    <section className="series-step-panel series-set-weekly" data-testid="weekly-entry-times">
      <header className="series-step-panel-header">
        <h3 className="series-step-panel-title">Weekly Entry Times</h3>
      </header>
      <div className="series-step-panel-body">
        <p className="series-edit-hint">
          Which ticket set each entry time uses in a typical week. Specific dates and monthly patterns can differ, so check
          the calendar above for those.
        </p>
        <div className="series-set-weekly-scroll">
          <table className="series-set-weekly-table">
            <thead>
              <tr>
                <th />
                {columns.map((day) => (
                  <th key={day}>{dayShort(day)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bands.map((band) => (
                <tr key={band.times[0]}>
                  <th scope="row">
                    <div>{bandLabel(band.times)}</div>
                    {band.times.length > 1 && <div className="series-edit-hint">{band.times.length} entry times</div>}
                  </th>
                  {band.cells.map((cell, index) =>
                    cell ? (
                      <td key={columns[index]}>
                        <WeeklyCell cell={cell} label={cellLabel(cell)} color={cell.key ? colorOf(sets, cell.key) : null} onJumpToSet={onJumpToSet} />
                      </td>
                    ) : (
                      <td key={columns[index]} className="series-set-weekly-empty">
                        {"—"}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

WeeklyEntryTimes.propTypes = {
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  cover: PropTypes.object.isRequired,
  onJumpToSet: PropTypes.func.isRequired,
};

export default WeeklyEntryTimes;
